import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, Typography, Button } from '@mui/material'
import { useSnackbar } from 'notistack'
import { Edit, XSquare } from 'react-feather'
import { useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm } from 'react-hook-form'
import { FormActionButtons } from '@/features/quotationCreate/FormActionButtons'
import { QuotationDetails } from '@/types'
import { QuotationNoteForm, QuotationNoteFormData } from './QuotationNoteForm'
import {
  usePatchQuotation,
  useMutationWrapper,
  getUseQuotationKey,
} from '@/hooks'

export type QuotationNoteFormContainerProps = {
  quotationDetails: QuotationDetails
}

export const QuotationNoteFormContainer = ({
  quotationDetails,
}: QuotationNoteFormContainerProps): JSX.Element => {
  const { t } = useTranslation('quotations')

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const queryClient = useQueryClient()

  const [edit, setEdit] = useState(false)

  const { mutateAsync: editQuotation, isLoading: isMutating } =
    useMutationWrapper(() => usePatchQuotation(quotationDetails.id))

  const defaultValues: QuotationNoteFormData = {
    note: quotationDetails.note ?? '',
  }

  const methods = useForm<QuotationNoteFormData>({
    mode: 'onChange',
    defaultValues: {},
    values: defaultValues,
  })

  const handleSubmit = methods.handleSubmit(async (formData) => {
    enqueueSnackbar('Saving...', { variant: 'default' })

    try {
      await editQuotation({ note: formData.note ?? '' })

      const quotationDetailsQueryKey = getUseQuotationKey(quotationDetails.id)

      await queryClient.invalidateQueries(quotationDetailsQueryKey)

      setEdit(false)

      closeSnackbar()
    } catch (e) {
      enqueueSnackbar(`${t('errors:api.execute')}: ${e}`, { variant: 'error' })
    }
  })

  const handleReset = () =>
    methods.reset({
      ...defaultValues,
    })

  return (
    <FormProvider {...methods}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('titles.note')}</Typography>
        <Button
          variant="text"
          onClick={() => {
            setEdit((v) => !v)
            handleReset()
          }}
          startIcon={!edit ? <Edit size="16px" /> : <XSquare size={14} />}
        >
          {!edit
            ? t('quotations:buttons.editNote')
            : t('quotations:buttons.closeEditor')}
        </Button>
      </Stack>

      {edit ? (
        <>
          <QuotationNoteForm />
          {methods.formState.isDirty && (
            <FormActionButtons
              cancelClick={handleReset}
              saveClick={handleSubmit}
              isLoading={isMutating}
              disabled={!methods.formState.isDirty}
            />
          )}
        </>
      ) : (
        <>{quotationDetails.note}</>
      )}
    </FormProvider>
  )
}
